/**
 * ranking-nacional.js — apresentação do menu "Ranking Nacional" (Brasil / Truck).
 *
 * Porte de biteams_app_vue/src/utils/rankingAntigo.js (Rankings > Ranking Nacional no biTeams), só
 * com o que o app do consultor usa. Aqui NÃO existe cálculo: população, ordem (métrica + desempate
 * em cascata), posição e "quem está sem resultado" vêm prontos da API (salesteams_app_api,
 * controllers/Rankings.php + models/rankings/Model_rankings.php, lista NACIONAL). Se uma regra
 * mudar, o que vale é o backend — os textos abaixo precisam ser atualizados junto.
 *
 * `path` é a rota do app (e a permissão da secretária); `endpoint` é a URI da API — iguais de
 * propósito, ver config/routes.php do salesteams_app_api. `menuLabel` é o rótulo exato do biTeams.
 */
import { canAccessRegionalMenu } from "@/utils/regional-user-permissions";

/** Rótulos dos critérios de desempate (ordem RK_ORDEM_DESEMPATE do biTeams). */
export const INDICADORES_NACIONAL = {
  boletosPagos: { label: "Boletos Quitados", curto: "Bol. Quitados", fmt: "int" },
  ticketMedio: { label: "Ticket Médio", curto: "Ticket Médio", fmt: "brl" },
  adesoes: { label: "Adesões", curto: "Adesões", fmt: "int" },
  consultoresAtivos: { label: "Consultores Ativos", curto: "Cons. Ativos", fmt: "int" },
  inadimplencia: { label: "Inadimplência", curto: "Inadimp.", fmt: "pct" },
};

/** Ordem do desempate em cascata (RK_ORDEM_DESEMPATE de rankingAntigoData.js). */
export const ORDEM_DESEMPATE = ["ticketMedio", "adesoes", "boletosPagos", "consultoresAtivos", "inadimplencia"];

export const GRUPOS_NACIONAL = [
  { key: "brasil", label: "Brasil" },
  { key: "truck", label: "Truck" },
];

function ranking(cfg) {
  return {
    ...cfg,
    path: `/biteams/rk-${cfg.id}`,
    endpoint: `biteams/rk-${cfg.id}`,
    metricLabel: INDICADORES_NACIONAL[cfg.metrica].label,
    metricCurto: INDICADORES_NACIONAL[cfg.metrica].curto,
    fmt: INDICADORES_NACIONAL[cfg.metrica].fmt,
    entidadeLabel: cfg.entidade === "consultor" ? "Consultor" : "Regional",
  };
}

/** Os 10 rankings, na ordem do menu do biTeams. */
export const RANKINGS_NACIONAIS = [
  ranking({
    id: "nacional-brasil-ho-boletos",
    grupo: "brasil",
    menuLabel: "Top 20 Home Office Boletos Quitados",
    titulo: "TOP 20 Home Office — Boletos Quitados",
    subtitulo: "Ranking mensal das regionais Home Office (base abaixo de 500) por boletos quitados.",
    entidade: "regional",
    metrica: "boletosPagos",
  }),
  ranking({
    id: "nacional-brasil-consultores-boletos",
    grupo: "brasil",
    menuLabel: "Top 20 Consultores Boletos Quitados",
    titulo: "TOP 20 Consultores — Boletos Quitados",
    subtitulo: "Ranking mensal de consultores (base menor que 300) por boletos quitados.",
    entidade: "consultor",
    metrica: "boletosPagos",
  }),
  ranking({
    id: "nacional-brasil-regional-adesao",
    grupo: "brasil",
    menuLabel: "Top 20 Regional Adesão",
    titulo: "TOP 20 Regional — Adesão",
    subtitulo: "Ranking mensal das regionais por adesões.",
    entidade: "regional",
    metrica: "adesoes",
  }),
  ranking({
    id: "nacional-brasil-regional-boletos",
    grupo: "brasil",
    menuLabel: "Top 20 Regional Boletos Quitados",
    titulo: "TOP 20 Regional — Boletos Quitados",
    subtitulo: "Ranking mensal das regionais por boletos quitados.",
    entidade: "regional",
    metrica: "boletosPagos",
  }),
  ranking({
    id: "nacional-brasil-regional-ticket",
    grupo: "brasil",
    menuLabel: "Top 20 Regional Ticket Médio",
    titulo: "TOP 20 Regional — Ticket Médio",
    subtitulo: "Ranking mensal das regionais por ticket médio — participam só as 50 primeiras do ranking de Adesão do mês.",
    entidade: "regional",
    metrica: "ticketMedio",
  }),
  ranking({
    id: "nacional-brasil-regional-inadimplencia",
    grupo: "brasil",
    menuLabel: "Top 20 Regional Inadimplência",
    titulo: "TOP 20 Regional — Inadimplência",
    subtitulo: "Ranking mensal das regionais por menor inadimplência — participam só as 50 regionais com mais adesões no mês.",
    entidade: "regional",
    metrica: "inadimplencia",
    // Menus de inadimplência desativados (pedido do negócio em 2026-11-04): some do menu, do
    // Resumo e do Regulamento; a rota redireciona (routes-list.js). Para religar, apague esta linha
    // e restaure a rota.
    desativado: true,
  }),
  ranking({
    id: "nacional-brasil-regional-cons-ativos",
    grupo: "brasil",
    menuLabel: "Top 20 Regional Consultores Ativos",
    titulo: "TOP 20 Regional — Consultores Ativos",
    subtitulo: "Ranking mensal das regionais por consultores ativos.",
    entidade: "regional",
    metrica: "consultoresAtivos",
  }),
  ranking({
    id: "nacional-truck-regional-adesao",
    grupo: "truck",
    menuLabel: "Top 10 Regional Adesão",
    titulo: "TOP 10 Regional Truck — Adesão",
    subtitulo: "Ranking mensal das regionais Truck por adesões.",
    entidade: "regional",
    metrica: "adesoes",
  }),
  ranking({
    id: "nacional-truck-regional-boletos",
    grupo: "truck",
    menuLabel: "Top 10 Regional Boletos Quitados",
    titulo: "TOP 10 Regional Truck — Boletos Quitados",
    subtitulo: "Ranking mensal das regionais Truck por boletos quitados.",
    entidade: "regional",
    metrica: "boletosPagos",
  }),
  ranking({
    id: "nacional-truck-consultor-adesao",
    grupo: "truck",
    menuLabel: "Top 10 Consultor Adesão",
    titulo: "TOP 10 Consultor Truck — Adesão",
    subtitulo: "Ranking mensal de consultores Truck (base menor que 300) por adesões.",
    entidade: "consultor",
    metrica: "adesoes",
  }),
].filter((r) => !r.desativado);

export const RANKINGS_NACIONAIS_POR_ID = RANKINGS_NACIONAIS.reduce((acc, r) => {
  acc[r.id] = r;
  return acc;
}, {});

/** Consultor: nem regional titular (`regional == 1`) nem secretária de regional. */
export function isConsultor(user) {
  return !!user && Number(user.regional) !== 1 && !user.regional_user;
}

/**
 * Pode abrir a tela deste ranking (Nacional ou Estadual)? Consultor não vê os rankings de Regional
 * (pedido do negócio em 2026-10-21) — só os de consultor; o Regulamento de todos continua aberto.
 * Vale só para a apresentação (menu, tela e atalhos): a API não filtra por perfil.
 */
export function podeVerRanking(user, ranking) {
  if (!ranking || !canAccessRegionalMenu(user, ranking.path)) return false;
  return !(ranking.entidade === "regional" && isConsultor(user));
}

/** Valor de um indicador no formato dele: inteiro, R$ ou %. */
export function fmtIndicador(valor, fmt) {
  if (valor === null || valor === undefined || isNaN(valor)) return "—";
  const n = Number(valor);
  if (fmt === "brl") {
    return "R$ " + n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (fmt === "pct") {
    return n.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";
  }
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
}

/**
 * "Faltam 3 boletos quitados" / "Faltam R$ 12,50 de ticket médio" / "Reduza a inadimplência em
 * 2,5 pontos percentuais". `valor` > 0 sempre (empate é tratado por quem chama).
 */
export function fmtFalta(valor, metrica) {
  const n = Number(valor) || 0;
  switch (metrica) {
    case "ticketMedio":
      return `Faltam ${fmtIndicador(n, "brl")} de ticket médio`;
    case "inadimplencia": {
      const pp = n.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      return `Reduza a inadimplência em ${pp} ${n < 2 ? "ponto percentual" : "pontos percentuais"}`;
    }
    case "boletosPagos":
      return n === 1 ? "Falta 1 boleto quitado" : `Faltam ${fmtIndicador(n, "int")} boletos quitados`;
    case "adesoes":
      return n === 1 ? "Falta 1 adesão" : `Faltam ${fmtIndicador(n, "int")} adesões`;
    case "consultoresAtivos":
      return n === 1 ? "Falta 1 consultor ativo" : `Faltam ${fmtIndicador(n, "int")} consultores ativos`;
    default:
      return `Faltam ${fmtIndicador(n, "int")}`;
  }
}
